import React, { useContext, useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Accordion from "react-bootstrap/Accordion";

import AccordionContext from "react-bootstrap/AccordionContext";
import { useAccordionToggle } from "react-bootstrap";
import { addReservations } from "../../../redux/slices/Member/member";
import {
  Container,
  StyledAccordion,
  ToggleIcon,
  ToggleTitle,
  ProgramDetails,
  MyStaysTitle,
  ActivityContainer,
} from "./ProfileMainHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProfileIntro from "../ProfileIntro/ProfileIntro";
import ProfileAddress from "../ProfileAddress/ProfileAddress";
import ReservationsList from "../../reservations/ReservationsList/ReservationsList";
import ReservationCancelDialog from "../../common/ReservationCancelDialog/ReservationCancelDialog";
import ProfileRewards from "../ProfileRewards/ProfileRewards";
import RewardsData from "../RewardsData/RewardsData";
import { gotoTab } from "../../../utils/helpers";
import { addPageTypeInGTMDataLayer } from "../../../utils/datalayers";
import { memberServiceProvider } from "../../../services/ServiceProvider";
import { useAppDispatch } from "../../../hooks";

const ContextAwareToggle = ({ children, eventKey, callback }: any) => {
  const currentEventKey = useContext(AccordionContext);

  const decoratedOnClick = useAccordionToggle(eventKey, (event) => {
    callback && callback(eventKey);
    const openedPanel = event.target;
    if (openedPanel) {
      // window.scrollTo(0, 0);
      gotoTab(`card-header-${eventKey}`);
    }
  });

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <Card.Header
      onClick={decoratedOnClick}
      className="open"
      id={`card-header-${eventKey}`}
    >
      {children}
      <ToggleIcon>
        {isCurrentEventKey ? (
          <FontAwesomeIcon icon="chevron-up" />
        ) : (
          <FontAwesomeIcon icon="chevron-down" />
        )}
      </ToggleIcon>
    </Card.Header>
  );
};

const ProfileMain = () => {
  const dispatch = useAppDispatch();
  const [pointActivityData, setPointActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState({
    PAST: true,
    CURRENT: true,
    CANCELLED: true,
  });

  const getPointActivity = async () => {
    const requestData = {
      sortBy: "transactiondate",
    };

    try {
      const memberService = await memberServiceProvider();
      const response = await memberService.getMemberActivityPoint(requestData);
      const resData = response?.memberPointsActivity?.results;
      setPointActivityData(resData);
    } catch (error) {
      console.error("Error fetching point activity data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMemberTripHistory = async (tripType: string) => {
    try {
      const memberService = await memberServiceProvider();
      const response = await memberService.getMemberTripHistory({ tripType });
      const results = response?.memberTripHistory?.results || [];
      dispatch(addReservations(results, tripType));
    } catch (error) {
      console.error(`Error fetching ${tripType} trip data:`, error);
    } finally {
      setIsLoading((prevLoading) => ({ ...prevLoading, [tripType]: false }));
    }
  };

  useEffect(() => {
    getMemberTripHistory("PAST");
    getMemberTripHistory("CURRENT");
    getMemberTripHistory("CANCELLED");
    getPointActivity();
  }, []);

  useEffect(() => {
    addPageTypeInGTMDataLayer("account");
  }, []);

  const AllReservations = () => {
    const [reservationCancelDialogConfig, setReservationCancelDialogConfig] =
      useState({
        openDialog: false,
        res: null,
      });

    const onDialogClose = () => {
      setReservationCancelDialogConfig({
        openDialog: false,
        res: null,
      });
    };

    return (
      <>
        <StyledAccordion>
          <MyStaysTitle>My Stays</MyStaysTitle>
          <Card key={0}>
            <ContextAwareToggle eventKey="0">
              <ToggleTitle id={"upcoming-stays"}>Upcoming Stays</ToggleTitle>
            </ContextAwareToggle>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                <ReservationsList
                  type="upcoming"
                  onCancel={setReservationCancelDialogConfig}
                  loading={isLoading.CURRENT}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card key={1}>
            <ContextAwareToggle eventKey="1">
              <ToggleTitle id={"recent-stays"}>Past Stays</ToggleTitle>
            </ContextAwareToggle>
            <Accordion.Collapse eventKey="1">
              <Card.Body>
                <ReservationsList type="recent" loading={isLoading.PAST} />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card key={2}>
            <ContextAwareToggle eventKey="2">
              <ToggleTitle id={"cancelled-reservations"}>
                Cancelled Reservations
              </ToggleTitle>
            </ContextAwareToggle>
            <Accordion.Collapse eventKey="2">
              <Card.Body>
                <ReservationsList
                  type="cancelled"
                  loading={isLoading.CANCELLED}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card key={3}>
            <ContextAwareToggle eventKey="3">
              <ToggleTitle id={"point-activity"}>Point Activity</ToggleTitle>
            </ContextAwareToggle>
            <Accordion.Collapse eventKey="3">
              <Card.Body>
                <ActivityContainer>
                  <RewardsData data={pointActivityData} loading={loading} />
                </ActivityContainer>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </StyledAccordion>
        <ReservationCancelDialog
          config={reservationCancelDialogConfig}
          onHide={onDialogClose}
        />
      </>
    );
  };

  return (
    <>
      <Container>
        <Row noGutters>
          <Col sm={12} lg={7} className="profileLHS">
            <ProfileIntro />
            <AllReservations />
          </Col>
          <Col sm={12} lg={{ span: 4, offset: 1 }} className="profileRHS">
            <Row>
              <Col>
                <ProfileRewards />
              </Col>
            </Row>

            <Row className={`mb-4`}>
              <Col>
                <ProfileAddress />
              </Col>
            </Row>
            <Row className={`mb-4`}>
              <Col>
                <ProgramDetails />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
export default ProfileMain;
